import React, { useEffect, useState } from "react";
import { AppLayout } from "../components/app/AppLayout";
import { importApi, auth } from "../lib/api";
import { Download, ShieldAlert } from "lucide-react";
import { TID } from "../constants/testIds";
import { QuarterlyRefreshTab } from "./import/QuarterlyRefreshTab";
import { MapsLinksTab } from "./import/MapsLinksTab";
import { LegacyBackfillTab } from "./import/LegacyBackfillTab";
import { MassReplaceTab } from "./import/MassReplaceTab";
import { ImportHistoryTable } from "./import/ImportHistoryTable";

/** Thin shell around the four import tabs. Each tab is self-contained
 *  and lives in `./import/`; this page only owns the tab selector,
 *  the download-template link, and the recent-imports history. */
export default function Import() {
  const [tab, setTab] = useState("current");
  const currentUser = auth.me();
  const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await importApi.history();
      setHistory(r.items || []);
    } catch (err) {
      console.error("[Import] history load failed:", err?.response?.status, err?.response?.data || err.message);
    }
  };
  useEffect(() => { loadHistory(); }, []);

  const tabs = [
    { k: "current", label: "Quarterly refresh" },
    { k: "maps", label: "Maps links (bulk update)" },
    { k: "legacy", label: "Legacy suspension backfill" },
    ...(isSuperAdmin ? [{ k: "replace", label: "Mass replacement", danger: true }] : []),
  ];

  return (
    <AppLayout mobileTitle="Import Data">
      <div className="mb-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Quarterly refresh
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Import Data
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Upload the quarterly Excel workbook. Descriptive fields are upserted;
          status &amp; audit history are never touched. Missing rows get flagged.
        </p>
      </div>

      <div className="mb-5 flex items-center gap-2 border-b border-slate-200 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.k}
            data-testid={t.k === "replace" ? TID.massReplaceTab : undefined}
            onClick={() => setTab(t.k)}
            className={`-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-xs font-semibold transition-colors ${
              tab === t.k
                ? (t.danger ? "border-red-600 text-red-700" : "border-nmi-700 text-nmi-700")
                : (t.danger ? "border-transparent text-red-600 hover:text-red-700" : "border-transparent text-slate-600 hover:text-slate-900")
            }`}
          >
            {t.danger && <ShieldAlert size={12} className="inline mr-1 -mt-0.5" />}
            {t.label}
          </button>
        ))}
        <a
          href={importApi.templateUrl()}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Download size={13} strokeWidth={2.5} /> Download template
        </a>
      </div>

      {tab === "current" && <QuarterlyRefreshTab onImported={loadHistory} />}
      {tab === "maps" && <MapsLinksTab onApplied={loadHistory} />}
      {tab === "legacy" && <LegacyBackfillTab />}
      {tab === "replace" && isSuperAdmin && <MassReplaceTab onExecuted={loadHistory} />}

      <ImportHistoryTable history={history} />
    </AppLayout>
  );
}
